import { useState } from 'react';
import { Camera, X, Play } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { ALL_PROOF_PHOTO_TYPES, ProofImages } from './ProofPackUpload';

interface ProofPackGalleryProps {
  proofImages: ProofImages;
  proofVideoUrl?: string | null;
  className?: string;
}

export function ProofPackGallery({
  proofImages,
  proofVideoUrl,
  className,
}: ProofPackGalleryProps) {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showVideo, setShowVideo] = useState(false);

  const availablePhotos = ALL_PROOF_PHOTO_TYPES.filter(pt => proofImages[pt.key]);

  if (availablePhotos.length === 0) return null;

  return (
    <>
      <div className={cn('space-y-4', className)}>
        {/* Photo Grid — only render slots the seller actually uploaded */}
        <div className="grid grid-cols-3 gap-2">
          {availablePhotos.map((photoType) => {
            const imageUrl = proofImages[photoType.key];
            if (!imageUrl) return null;


            return (
              <button
                key={photoType.key}
                onClick={() => setSelectedImage(imageUrl)}
                className="aspect-square rounded-lg overflow-hidden border border-border/50 hover:border-white/20 transition-colors relative group"
              >
                <img
                  src={imageUrl}
                  alt={photoType.label}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                <span className="absolute bottom-1 left-1 text-[9px] bg-black/60 text-white px-1 rounded">
                  {photoType.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Video button */}
        {proofVideoUrl && (
          <button
            onClick={() => setShowVideo(true)}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-border/50 hover:border-white/20 bg-muted/30 transition-colors"
          >
            <Play className="w-4 h-4 text-primary" />
            <span className="text-sm">Watch Video Sweep</span>
          </button>
        )}

      </div>

      {/* Lightbox for images */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent variant="fullscreen" className="max-w-3xl p-0 overflow-hidden bg-black/90">
          <DialogHeader className="sr-only">
            <DialogTitle>Proof Photo</DialogTitle>
          </DialogHeader>
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 z-10"
          >
            <X className="w-5 h-5" />
          </button>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Proof photo"
              className="w-full h-auto max-h-[80dvh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Video modal */}
      <Dialog open={showVideo} onOpenChange={setShowVideo}>
        <DialogContent variant="fullscreen" className="max-w-3xl p-0 overflow-hidden bg-black/90">
          <DialogHeader className="sr-only">
            <DialogTitle>Proof Video</DialogTitle>
          </DialogHeader>
          {proofVideoUrl && showVideo && (
            <video
              src={proofVideoUrl}
              controls
              autoPlay
              className="w-full h-auto max-h-[80dvh]"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
